import { Avatar, Tooltip } from "antd"
import { fetchCareStatuses, mapCareStatusToLabel } from "../../pages/RoomChat/chatLabels"
import { Pin, MessageCircle, MessageSquareText, Reply } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useState, useEffect } from "react"
import { StarFilled } from "@ant-design/icons"
import { cleanStaffName } from "../../api/ChannelMessage"

export default function ConversationItem({
    data,
    active,
    onClick
}: {
    data: { avatar: string; name: string; time: string; labels?: string[]; lastMessage?: string; lastMessageStaff?: string; direction?: string | null; unreadCount?: number; isPriority?: boolean; isPinned?: boolean; type?: string }
    active: boolean
    onClick: () => void
}) {
    const { t } = useTranslation()
    const [CHAT_LABELS, setChatLabels] = useState<any[]>([])

    // Khách đến từ bình luận bài viết khác với khách nhắn tin trực tiếp:
    // trả lời đi qua API khác nên cần nhìn ra ngay từ danh sách.
    const isComment = data.type === "comment"

    // API trả direction của tin nhắn cuối: "outbound" = nhân viên đã trả lời,
    // "inbound" = khách gửi và chưa ai phản hồi. Không có direction thì không đoán.
    const isAwaitingReply = data.direction === "inbound"

    // API tự gắn "Bạn: " trước tin cuối của phía page; khi đã có tên nhân viên thì
    // bỏ tiền tố đó cho khỏi thành "Tên NV: Bạn: ..."
    const staffPrefix = !isAwaitingReply ? cleanStaffName(data.lastMessageStaff) : ""
    const previewText = (staffPrefix
        ? (data.lastMessage || "").replace(/^Bạn:\s*/, "")
        : data.lastMessage) || "Tập đính kèm"

    useEffect(() => {
        fetchCareStatuses().then((items) => {
            setChatLabels(items.map(mapCareStatusToLabel));
        });
    }, []);

    // Thẻ hiển thị ở hàng riêng bên dưới và cho xuống dòng, nên không cần cắt bớt
    const activeLabels = data.labels
        ?.map((key: string) => CHAT_LABELS.find(l => l.key === key))
        .filter(Boolean) || []

    return (
        <div
            onClick={onClick}
            className={`
                group relative
                flex items-center justify-between px-3 py-3
                cursor-pointer border-b
                hover:!bg-[#fab21625]
                dark:hover:bg-[#fab21625]
                border-color-1f1f1f
                ${active ? "bg-[#fab21625] dark:bg-[#fab21625]" : ""}
            `}
        >

            <div className="flex items-center gap-3 w-full">

                <div style={{ position: 'relative' }}>
                    <Avatar src={data.avatar || undefined} className="w-[40px] flex-shrink-0 h-[40px] dark:bg-[#2a2a2a]">
                        {data.name?.charAt(0) || "?"}
                    </Avatar>
                    {data.isPriority && (
                        <StarFilled style={{ position: 'absolute', top: -2, right: -2, color: '#faad14', fontSize: 12 }} />
                    )}

                    {data.type && (
                        <Tooltip title={isComment ? t('chats.type_comment') : t('chats.type_chat')}>
                            <span
                                className="absolute -bottom-[2px] -right-[2px] w-[16px] h-[16px] rounded-full flex items-center justify-center border-2 border-white dark:border-[#1f1f1f]"
                                style={{ background: isComment ? "#f59e0b" : "#3e91ab" }}
                            >
                                {isComment
                                    ? <MessageSquareText size={9} color="#ffffff" />
                                    : <MessageCircle size={9} color="#ffffff" />}
                            </span>
                        </Tooltip>
                    )}
                </div>

                <div className="flex flex-col flex-1 min-w-0">

                    <div className="flex gap-2 justify-between items-center">
                        <div className="flex gap-2 items-center min-w-0 flex-1">
                            <Tooltip title={data.name || "Khách hàng"}>
                                <span className="text-[14px] text-000000e0 truncate block">
                                    {data.name || "Khách hàng"}
                                </span>
                            </Tooltip>

                            {/* Tạm ẩn tính năng ghim (sẽ mở lại sau)
                            {data.isPinned && (
                                <Pin
                                    size={12}
                                    className="text-[#3e91ab] flex-shrink-0"
                                    fill="#3e91ab"
                                />
                            )}
                            */}
                        </div>

                        <div className="text-[12px] text-000000e0 whitespace-nowrap flex-shrink-0">
                            {data.time?.split(" ")[0]}
                        </div>
                    </div>

                    <Tooltip
                        title={isAwaitingReply
                            ? `${t('chats.awaiting_reply')} — ${data.lastMessage}`
                            : staffPrefix
                                ? <><div className="font-medium">{staffPrefix}</div><div>{previewText}</div></>
                                : previewText}
                    >
                        <div className="min-w-0">
                            {/* Nhân viên CRM gửi tin cuối -> tên một dòng, nội dung xuống dòng dưới */}
                            {staffPrefix && (
                                <div className="text-[12px] font-medium text-[#3e91ab] truncate">
                                    {staffPrefix}
                                </div>
                            )}
                            <div
                                style={{
                                    textAlign: "start",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    color: "rgb(74, 100, 108)",
                                    fontSize: "12px"
                                }}
                            >
                                {/* Tin cuối là của khách -> đánh dấu để quản trị thấy ngay là chưa trả lời */}
                                {isAwaitingReply && (
                                    <Reply
                                        size={12}
                                        className="inline-block mr-1 align-[-1px] text-[#dc2626] flex-shrink-0"
                                    />
                                )}
                                {previewText}
                            </div>
                        </div>
                    </Tooltip>

                    <div className="flex items-center justify-between mt-1 gap-2">

                        {/* LABELS — xuống dòng khi nhiều thẻ để không bị cắt mất */}
                        <div className="flex gap-1 items-center flex-wrap min-w-0 flex-1">
                            {activeLabels.map((label: any) => (
                                <Tooltip key={label.key} title={label.label}>
                                    <span
                                        className="text-[10px] px-1 h-[16px] leading-[16px] rounded text-white max-w-full truncate"
                                        style={{ background: label.color }}
                                    >
                                        {label.label}
                                    </span>
                                </Tooltip>
                            ))}
                        </div>

                        {/* ICON - Tạm ẩn nút ghim nhanh (sẽ mở lại sau)
                        <div className="
                            absolute right-2 bottom-2 bg-white hover:bg-gray-150 border border-gray-200 p-1 rounded-md group-hover/item:opacity-100 transition-opacity flex items-center justify-center shadow-xs
                            d-none group-hover:!block
                        ">
                            <Pin
                                size={12}
                                className={`
                                ${data.isPinned ? "text-[#3e91ab]" : "text-gray-400"}`
                                }
                                fill={data.isPinned ? "#3e91ab" : "#cccccc"}
                            />
                        </div>
                        */}

                        {!!data.unreadCount && data.unreadCount > 0 && (
                            <div className="min-w-[16px] h-[16px] px-1 rounded-full bg-[#dc2626] text-[#ffffff] text-[10px] flex items-center justify-center text-center flex-shrink-0">
                                {data.unreadCount > 99 ? "99+" : data.unreadCount}
                            </div>
                        )}

                    </div>

                </div>

            </div>

        </div>
    )
}